import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import LogoutIcon from "@mui/icons-material/Logout";
import ArticleIcon from "@mui/icons-material/Article";
import ChatIcon from "@mui/icons-material/Chat";
import ForwardIcon from "@mui/icons-material/Forward";
import FeedbackIcon from "@mui/icons-material/Feedback";
import PendingIcon from "@mui/icons-material/Pending";
import BeenhereIcon from "@mui/icons-material/Beenhere";
import RateReviewIcon from "@mui/icons-material/RateReview";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import { Dashboard, Public, Help } from "@mui/icons-material";
import ApprovalIcon from "@mui/icons-material/Approval";
import { styled, useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import MenuIcon from "@mui/icons-material/Menu";
import IconButton from "@mui/material/IconButton";
import NotificationSnackbar from "./NotificationBar";
import Badge from "@mui/material/Badge";
import CircleIcon from "@mui/icons-material/Circle";
import OpenInBrowserIcon from "@mui/icons-material/OpenInBrowser";


const menus = [
  { name: "Dashboard and Reports", icon: <Dashboard /> },
  { name: "Pending Tickets", icon: <PendingIcon /> },
  { name: "Assigned Tickets", icon: <ForwardIcon /> },
  { name: "Responded Tickets", icon: <BeenhereIcon /> },
  { name: "Request User feedbacks", icon: <RateReviewIcon /> },
  { name: "Publish Module", icon: <Public /> },
  { name: "Publish Article", icon: <ArticleIcon /> },
  { name: "Frequently Asked Questions", icon: <Help /> },
  { name: "User FeedBacks", icon: <FeedbackIcon /> },
  { name: "Request Appointment", icon: <ApprovalIcon /> },
  { name: "Open RBM Exceptions", icon: <OpenInBrowserIcon /> },
  { name: "Mapping Grobal Positions", icon: <AccountTreeIcon /> },
  { name: "IT Services Request", icon: <AccountTreeIcon /> },
  { name: "Chats", icon: <ChatIcon /> },
  { name: "LogOut", icon: <LogoutIcon /> },
];

// Only users of this institution may see/open "IT Services Request"
const IT_SERVICES_INSTITUTION = "MINISTRY OF PUBLIC SERVICE AND LABOUR";

// Ignores letter case, extra spaces and a trailing "(Ministry)"
const normalizeInstitution = (value) =>
  String(value ?? "")
    .replace(/\s*\(.*\)\s*$/, "")
    .replace(/\s+/g, " ")
    .trim()
    .toUpperCase();

const SidebarContainer = styled(Box)(({ theme, $collapsed, $mobileOpen }) => ({
  width: $collapsed ? "80px" : "280px",
  height: "100vh",
  position: "fixed",
  zIndex: theme.zIndex.drawer,
  backgroundColor: theme.palette.primary.dark,
  backgroundImage: "linear-gradient(195deg, #0a2e52 0%, #1565C0 100%)",
  boxShadow:
    "0 4px 20px 0 rgba(0, 0, 0, 0.14), 0 7px 10px -5px rgba(0, 0, 0, 0.4)",
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.easeInOut,
    duration: theme.transitions.duration.standard,
  }),
  overflowY: "auto",
  "&::-webkit-scrollbar": {
    width: "6px",
  },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor: "rgba(255, 255, 255, 0.2)",
    borderRadius: "3px",
  },
  [theme.breakpoints.down("sm")]: {
    width: $mobileOpen ? "280px" : 0,
    transform: $mobileOpen ? "translateX(0)" : "translateX(-280px)",
    transition: theme.transitions.create(["width", "transform"], {
      easing: theme.transitions.easing.easeInOut,
      duration: theme.transitions.duration.standard,
    }),
  },
}));

const MenuItem = styled(ListItemButton)(({ theme, $selected, $collapsed }) => ({
  padding: theme.spacing(1.5, $collapsed ? 2 : 3),
  margin: theme.spacing(0.5, 1.5),
  borderRadius: "8px",
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  justifyContent: $collapsed ? "center" : "flex-start",
  backgroundColor: $selected ? "rgba(255, 255, 255, 0.9)" : "transparent",
  color: $selected ? theme.palette.primary.dark : theme.palette.common.white,
  "&:hover": {
    backgroundColor: $selected
      ? "rgba(255, 255, 255, 0.95)"
      : "rgba(255, 255, 255, 0.2)",
    color: $selected ? theme.palette.primary.dark : theme.palette.common.white,
    transform: "translateX(4px)",
    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
  },
  "& .MuiListItemIcon-root": {
    minWidth: $collapsed ? "auto" : 36,
    color: "inherit",
    transition: "all 0.3s ease",
  },
  [theme.breakpoints.down("sm")]: {
    justifyContent: "flex-start",
    padding: theme.spacing(1.5, 3),
  },
}));

const MobileToggleButton = styled(IconButton)(({ theme }) => ({
  position: "fixed",
  left: 16,
  top: 16,
  zIndex: theme.zIndex.drawer + 2,
  color: theme.palette.common.white,
  backgroundColor: theme.palette.primary.dark,
  "&:hover": {
    backgroundColor: theme.palette.primary.main,
  },
  boxShadow: theme.shadows[4],
}));

function LeftToolbar({ selectedMenu, onSelectMenu, onCollapse }) {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activeMenu, setActiveMenu] = useState(selectedMenu);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [assignedTicketsCount, setAssignedTicketsCount] = useState(0);

  const [user] = useState(() => {
    try {
      const userData = localStorage.getItem("userData");
      return userData ? JSON.parse(userData) : {};
    } catch (error) {
      console.error("Error parsing user data:", error);
      return {};
    }
  });

  const userRole = String(user?.roleId ?? "")
    .trim()
    .toUpperCase();

  // Institution rule for the "IT Services Request" menu
  const canAccessITServicesRequest =
    normalizeInstitution(user?.institutionName) === IT_SERVICES_INSTITUTION;

  // TEMPORARY: remove once the menu shows for the right users
  // console.log("MENU CHECK", {
  //   roleId: user?.roleId,
  //   institutionName: user?.institutionName,
  //   canAccessITServicesRequest,
  // });

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const handleSnackbarOpen = (message, severity = "success") => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const fetchTickets = async () => {
    try {
      const res = await fetch("/api/support/assignedTickets", {
        method: "GET",
        credentials: "include",
      });
      const data = await res.json();
      if (!res.ok || !Array.isArray(data)) {
        throw new Error("The assigned tickets response is not valid.");
      }

      const currentUserFullName =
        `${user?.firstName} ${user?.lastName}`.trim();

      const currentUserId = user?.userId || user?.id;

      const userAssignedTickets = data.filter((ticket) => {
        const nameMatch =
          ticket.AssignedTo?.trim().toLowerCase() ===
          currentUserFullName.toLowerCase();

        const idMatch =
          ticket.personAssignedTo === currentUserId ||
          ticket.userId === currentUserId;

        return nameMatch || idMatch;
      });

      setAssignedTicketsCount(userAssignedTickets.length);
    } catch (err) {
      console.error("Error fetching tickets:", err);
    }
  };

  useEffect(() => {
    fetchTickets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const path = location.pathname;

    const menuMap = {
      "/CreateModule": "Publish Module",
      "/Dashboard": "Dashboard and Reports",
      "/CreateQuestions": "Frequently Asked Questions",
      "/PendingTickets": "Pending Tickets",
      "/CreateArticle": "Publish Article",
      "/ChatSection": "Chats",
      "/AssignedTickets": "Assigned Tickets",
      "/RequestUserfeedbacks": "Request User feedbacks",
      "/RespondedTickets": "Responded Tickets",
      "/RequestAppointment": "Request Appointment",
      "/MappingGrobalPositions": "Mapping Grobal Positions",
      "/ITServicesRequest": "IT Services Request",
      "/RBMExceptions": "Open RBM Exceptions",
      "/UserFeedBacks": "User FeedBacks",
    };

    const currentMenu = Object.entries(menuMap).find(([route]) =>
      path.startsWith(route)
    )?.[1];

    if (currentMenu) {
      setActiveMenu(currentMenu);

      if (onSelectMenu) {
        onSelectMenu(currentMenu);
      }
    }
  }, [location.pathname, onSelectMenu]);

  const handleLogout = async () => {
    try {
      const response = await fetch("/api/auth/logout", {
        method: "POST",
        credentials: "include",
      });

      if (response.ok) {
        localStorage.removeItem("isLoggedIn");
        localStorage.removeItem("userToken");
        localStorage.removeItem("userData");
        navigate("/");
      } else {
        const data = await response.json();
        handleSnackbarOpen(data.message || "Logout failed", "error");
      }
    } catch (error) {
      console.error("Logout error:", error);
      handleSnackbarOpen("Logout error", "error");
    }
  };

  const roleAccessMap = {
    "Dashboard and Reports": ["ADMIN", "SUPPORT"],
    "Publish Module": ["ADMIN", "SUPPORT"],
    "Frequently Asked Questions": ["ADMIN", "SUPPORT"],
    "Pending Tickets": [
      "ADMIN",
      "SUPPORT",
      "APPLICANT",
      "SUPPORT_AGENT",
      "EMPLOYEE",
    
    ],
    "Request Appointment": ["ADMIN", "SUPPORT"],
    "Publish Article": ["ADMIN", "SUPPORT"],
    "Request User feedbacks": ["ADMIN", "SUPPORT"],
    "User FeedBacks": ["ADMIN", "SUPPORT"],
    Chats: ["ADMIN", "SUPPORT", "SUPPORT_AGENT"],
    "Assigned Tickets": ["ADMIN", "SUPPORT", "SUPPORT_AGENT"],
    "Mapping Grobal Positions": ["ADMIN", "SUPPORT", "SUPPORT_AGENT"],
    "Open RBM Exceptions": ["ADMIN", "SUPPORT", "SUPPORT_AGENT"],
    "Responded Tickets": [
      "ADMIN",
      "SUPPORT",
      "APPLICANT",
      "SUPPORT_AGENT",
      "EMPLOYEE",
      "NO_ROLE",
      
    ],
    "IT Services Request": ["ADMIN", "ITSERVICES"],
    LogOut: [
      "ADMIN",
      "SUPPORT",
      "APPLICANT",
      "SUPPORT_AGENT",
      "NO_ROLE",
      "EMPLOYEE",
      "ITSERVICES",
    ],
  };

  useEffect(() => {
    const handleResize = () => {
      if (!isMobile && mobileOpen) {
        setMobileOpen(false);
      }
    };

    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
  }, [isMobile, mobileOpen]);

  const handleMenuClick = (menuName) => {
    // IT Services Request: Ministry of Public Service and Labour only
    if (menuName === "IT Services Request" && !canAccessITServicesRequest) {
      handleSnackbarOpen(
        "Access Denied: IT Services Request is only for the Ministry of Public Service and Labour.",
        "warning"
      );
      return;
    }

    // IT Services Request is controlled by institution only, not by role
    const allowedRoles =
      menuName === "IT Services Request" ? undefined : roleAccessMap[menuName];

    if (
      allowedRoles &&
      allowedRoles !== "ALL" &&
      !allowedRoles.includes(userRole)
    ) {
      handleSnackbarOpen(
        "Access Denied: You are not authorized for this menu.",
        "warning"
      );
      return;
    }

    setActiveMenu(menuName);

    if (onSelectMenu) {
      onSelectMenu(menuName);
    }

    if (menuName === "LogOut" || menuName === "Home") {
      handleLogout();
    } else if (menuName === "Publish Module") {
      navigate("/CreateModule");
    } else if (menuName === "Dashboard and Reports") {
      navigate("/Dashboard");
    } else if (menuName === "Frequently Asked Questions") {
      navigate("/CreateQuestions");
    } else if (menuName === "Pending Tickets") {
      navigate("/PendingTickets");
    } else if (menuName === "Publish Article") {
      navigate("/CreateArticle");
    } else if (menuName === "Chats") {
      navigate("/ChatSection");
    } else if (menuName === "Assigned Tickets") {
      navigate("/AssignedTickets");
    } else if (menuName === "Request User feedbacks") {
      navigate("/RequestUserfeedbacks");
    } else if (menuName === "Responded Tickets") {
      navigate("/RespondedTickets");
    } else if (menuName === "Request Appointment") {
      navigate("/RequestAppointment");
    } else if (menuName === "Mapping Grobal Positions") {
      navigate("/MappingGrobalPositions");
    } else if (menuName === "Open RBM Exceptions") {
      navigate("/RBMExceptions");
    } else if (menuName === "User FeedBacks") {
      navigate("/UserFeedBacks");
    } else if (menuName === "IT Services Request") {
      navigate("/ITServicesRequest");
    }

    if (isMobile) {
      setMobileOpen(false);
    }
  };

  const isMenuAllowed = (menuName) => {
    // IT Services Request: the institution is the ONLY rule (no role needed)
    if (menuName === "IT Services Request") {
      return canAccessITServicesRequest;
    }

    if (["ADMIN", "SUPPORT"].includes(userRole)) return true;

    const allowedRoles = roleAccessMap[menuName];

    if (!allowedRoles || allowedRoles.includes("ALL")) return true;

    return allowedRoles.includes(userRole);
  };

  const toggleSidebar = () => {
    if (isMobile) {
      setMobileOpen(!mobileOpen);
    } else {
      const newCollapsed = !collapsed;
      setCollapsed(newCollapsed);

      if (onCollapse) {
        onCollapse(newCollapsed);
      }
    }
  };

  const getMenuItems = () => {
    return menus.map((menu) => {
      if (menu.name === "Assigned Tickets") {
        return {
          ...menu,
          badge:
            assignedTicketsCount > 0 ? (
              <Badge
                badgeContent={assignedTicketsCount}
                color="error"
                overlap="circular"
                sx={{
                  "& .MuiBadge-badge": {
                    right: -110,
                    top: 0,
                    backgroundColor: theme.palette.error.main,
                    color: theme.palette.error.contrastText,
                    fontSize: "0.7rem",
                    fontWeight: "bold",
                    minWidth: "20px",
                    height: "20px",
                    borderRadius: "10px",
                  },
                }}
              >
                {menu.icon}
              </Badge>
            ) : (
              menu.icon
            ),
        };
      }

      return menu;
    });
  };

  if (isMobile && !mobileOpen) {
    return (
      <MobileToggleButton onClick={toggleSidebar}>
        <MenuIcon />
      </MobileToggleButton>
    );
  }

  const showLabels = !collapsed || isMobile;

  return (
    <>
      {isMobile && (
        <MobileToggleButton
          onClick={toggleSidebar}
          sx={{ left: mobileOpen ? 280 : 16 }}
        >
          {mobileOpen ? <ChevronLeftIcon /> : <MenuIcon />}
        </MobileToggleButton>
      )}

      <SidebarContainer $collapsed={collapsed} $mobileOpen={mobileOpen}>
        <List sx={{ pt: isMobile ? 8 : 4 }}>
          {getMenuItems()
            .filter((menu) => isMenuAllowed(menu.name))
            .map((menu) => (
              <React.Fragment key={menu.name}>
                <MenuItem
                  onClick={() => handleMenuClick(menu.name)}
                  $selected={menu.name === activeMenu}
                  $collapsed={collapsed && !isMobile}
                >
                  {React.cloneElement(menu.badge || menu.icon, {
                    fontSize: collapsed && !isMobile ? "medium" : "small",
                  })}

                  {showLabels && (
                    <ListItemText
                      primary={
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          {menu.name}

                          {menu.name === "Assigned Tickets" &&
                            assignedTicketsCount > 0 && (
                              <CircleIcon
                                sx={{
                                  position: "absolute",
                                  color: theme.palette.error.main,
                                  fontSize: "8px",
                                  right: 202,
                                  top: 20,
                                }}
                              />
                            )}
                        </Box>
                      }
                      sx={{
                        marginLeft: 1.5,
                        "& .MuiTypography-root": {
                          fontWeight: menu.name === activeMenu ? 600 : 500,
                          fontSize: "0.875rem",
                        },
                      }}
                    />
                  )}

                </MenuItem>

              </React.Fragment>
            ))}
        </List>

        <Divider
          sx={{
            backgroundColor: "rgba(255, 255, 255, 0.12)",
            mx: 2,
          }}
        />

        <NotificationSnackbar
          open={snackbarOpen}
          onClose={handleSnackbarClose}
          message={snackbarMessage}
          severity={snackbarSeverity}
        />
      </SidebarContainer>
    </>
  );
}

export default LeftToolbar;